import React, { useState } from "react";
import { verifyCode, sendVerification } from "../../auth/services/authService";
import VerificationInput from "react-verification-input";
import Button from "../../common/components/Button";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import {
  ArrowPathIcon,
  ExclamationCircleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import PropTypes from "prop-types";

const EmailVerificationModal = ({ isOpen, onClose, email, name }) => {
  const [verificationCode, setVerificationCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [isDisabled, setIsDisabled] = useState(true);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    console.log("Verifying code in EmailVerificationModal:", verificationCode);
    const result = await verifyCode({
      userCode: verificationCode,
    });
    if (result.success) {
      onClose("success");
    } else {
      setError(result.errors?.code || "Invalid verification code");
    }
    setLoading(false);
  };

  const handleSendAgain = async () => {
    setLoading(true);
    setError(null);
    const result = await sendVerification({ email, name });
    if (result.success) {
      setError("New verification code sent!");
    } else {
      setError("Failed to send new code. Please try again.");
    }
    setLoading(false);
  };

  const onComplete = (value) => {
    console.log("onComplete in EmailVerificationModal:", value);
    setIsDisabled(false);
    setVerificationCode(value);
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onClose={() => {}} className="relative z-25">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-lightGray/75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
      />
      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <DialogPanel
            transition
            className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-lg data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
            data-testid="verification-modal"
          >
            <div className="absolute right-0 top-0 pr-4 pt-4">
              <button
                type="button"
                className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none"
                onClick={() => onClose("cancel")}
                data-testid="close-modal-button"
              >
                <span className="sr-only">Close</span>
                <XMarkIcon className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>
            <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex size-12 shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:size-10">
                  <ExclamationCircleIcon
                    aria-hidden="true"
                    className="size-6 text-yellow-500"
                  />
                </div>
                <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                  <DialogTitle
                    as="h3"
                    className="text-base font-semibold text-gray-900 font-sans"
                  >
                    Verify your email
                  </DialogTitle>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500 font-sans mb-4">
                      Please enter the verification code sent to {email}. If you
                      do not see it after a few minutes, check your spam folder.
                    </p>
                  </div>

                  <div className="flex flex-col gap-4 align-middle">
                    {loading ? (
                      <div className="flex justify-center">
                        <ArrowPathIcon
                          className="size-6 animate-spin"
                          aria-label="loading"
                        />
                      </div>
                    ) : (
                      <div className="flex sm:justify-start justify-center">
                        <VerificationInput
                          length={6}
                          onComplete={(value) => onComplete(value)}
                          placeholder="."
                          autoFocus={true}
                          validChars="0-9"
                          className="font-sans"
                          inputProps={{
                            "aria-label": "verification code",
                            role: "textbox",
                            "data-testid": "verification-input",
                          }}
                        />
                      </div>
                    )}
                    {error && (
                      <div
                        className="mt-4 flex items-center text-red-600 bg-red-50 p-3 rounded-md"
                        data-testid="modal-error"
                      >
                        <ExclamationCircleIcon className="h-5 w-5 mr-2" />
                        <span>{error}</span>
                      </div>
                    )}
                    <div className="flex flex-row gap-2 sm:justify-start justify-center">
                      <Button
                        className="font-sans"
                        onClick={handleSendAgain}
                        text="Send Again"
                        color="primary"
                        disabled={loading}
                        data-testid="resend-button"
                      />
                      <Button
                        className="font-sans"
                        onClick={(e) => handleSubmit(e)}
                        text="Verify"
                        color="primary"
                        disabled={isDisabled || loading}
                        loading={loading}
                        data-testid="verify-button"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
};

EmailVerificationModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  email: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
};

export default EmailVerificationModal;
